/**
 * Amplify Configuration Helper
 * Supports local mock, demo mode, and deployed AWS environments
 * Updated for AWS Amplify v6
 */

import type { ResourcesConfig } from 'aws-amplify';

// Get config from environment variables (set by Amplify/Terraform) or fallback to defaults
const COGNITO_REGION = process.env.REACT_APP_COGNITO_REGION || 'us-east-1';
const COGNITO_USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_7bGJQY81q';
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || 'lu10ihtva9haib1bamb4omt78';
const COGNITO_IDENTITY_POOL_ID = process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID || 'us-east-1:0ffd63a0-b58e-4c68-9353-fdbb663eb87d';
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || 'crt-co-prod-auth';
const APPSYNC_ENDPOINT = process.env.REACT_APP_APPSYNC_ENDPOINT || 'https://bzmmutmlkvfztojkpmzyus5xae.appsync-api.us-east-1.amazonaws.com/graphql';
const APPSYNC_API_KEY = process.env.REACT_APP_APPSYNC_API_KEY || 'da2-klhzwsmxmfhi3ikm73tx5rkqpy';
const S3_BUCKET = process.env.REACT_APP_S3_BUCKET || 'crt-co-s3-prod-uploads';

// AWS Configuration
const awsConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: COGNITO_USER_POOL_ID,
      userPoolClientId: COGNITO_CLIENT_ID,
      identityPoolId: COGNITO_IDENTITY_POOL_ID,
      loginWith: {
        email: true
      }
    }
  },
  API: {
    GraphQL: {
      endpoint: APPSYNC_ENDPOINT,
      region: COGNITO_REGION,
      defaultAuthMode: 'userPool',
      apiKey: APPSYNC_API_KEY
    }
  },
  Storage: {
    S3: {
      bucket: S3_BUCKET,
      region: COGNITO_REGION
    }
  }
};

// Export for use in authConfig
export const cognitoDomain = `${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com`;

// Mock configuration for local development
const mockConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: 'us-east-1_7bGJQY81q',
      userPoolClientId: 'lu10ihtva9haib1bamb4omt78'
    }
  },
  API: {
    GraphQL: {
      endpoint: 'http://localhost:20002/graphql',
      region: 'us-east-1',
      defaultAuthMode: 'apiKey',
      apiKey: 'da2-fakeApiId123456'
    }
  }
};

// Check if running with Amplify mock
export const isAmplifyMock = (): boolean => {
  return process.env.REACT_APP_USE_AMPLIFY_MOCK === 'true';
};

// Check if demo mode is enabled
export const isDemoModeEnabled = (): boolean => {
  return process.env.REACT_APP_DEMO_MODE === 'true';
};

// Get the appropriate Amplify configuration for v6
export const getAmplifyConfig = (): ResourcesConfig => {
  if (isAmplifyMock()) {
    console.log('[Config] Using Amplify Mock configuration');
    return mockConfig;
  }
  
  // Production/Dev - use real AWS config
  console.log('[Config] Using AWS configuration');
  return awsConfig;
};

// Environment info for debugging
export const getEnvironmentInfo = () => {
  return {
    isDemoMode: isDemoModeEnabled(),
    isAmplifyMock: isAmplifyMock(),
    isProduction: !isAmplifyMock() && !isDemoModeEnabled(),
    graphqlEndpoint: isAmplifyMock() 
      ? 'http://localhost:20002/graphql'
      : awsConfig.API?.GraphQL?.endpoint,
    region: 'us-east-1',
    environment: process.env.NODE_ENV
  };
};

export default getAmplifyConfig;
